import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  List,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CancelIcon from "@material-ui/icons/Cancel";

import { BACKDROP } from "./SurveysDialog";
import SupportSatisfactionChart from "../components/charts/SupportSatisfactionChart";
import { useTranslation } from "react-i18next";
import SolutionsSatisfactionChart from "../components/charts/SolutionsSatisfactionChart";
import {
  COMMENT_NEGATIVE,
  COMMENT_NEUTRAL,
  COMMENTS,
  NEXT_ACTIONS,
  SHOW_SURVEY_RESULTS,
} from "../lib/results";
import { isNil } from "lodash";
import { useSurveysStore } from "../state/SurveysState";

const useStyles = makeStyles((theme) => ({
  dialogBackdrop: {
    ...BACKDROP,
  },
  dialogPaper: {
    minWidth: "900px",
    maxWidth: "1250px",
    backgroundColor: "transparent",
    borderRadius: 10,
    [theme.breakpoints.down("md")]: {
      maxWidth: "90%",
      minWidth: "90%",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "95%",
      minWidth: "95%",
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    minHeight: "550px",
    display: "flex",
    flexDirection: "column",
    background: theme.palette.survey.backgroundColor,
    alignItems: "center",
    justifyContent: "flex-start",
  },
  dialogContentHi: {
    padding: theme.spacing(3),
    height: "auto",
    display: "flex",
    flexDirection: "column",
    background: theme.palette.survey.backgroundColor,
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(4),
  },
  chartsContainer: {
    height: "500px",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      height: "auto",
    },
  },
  supportChart: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    gap: 16,
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      height: "500px",
    },
  },
  solutionsChart: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    gap: 16,
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      height: "500px",
    },
  },
  hiBox: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    gap: 16,
    alignItems: "flex-start",
    paddingTop: 16,
  },
  stepActions: {
    height: "50px",
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBlock: "auto",
  },
  list: {
    padding: 12,
  },
  listItem: {
    marginBottom: 12,
  },
}));

export default function SurveysResultDialog() {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    showSurveyResult,
    closeShowSurveyResultDialog,
    showSurveyResultSkipped,
  } = useSurveysStore();
  const [step, setStep] = useState(0);

  const goNext = () => {
    setStep((prev) => prev + 1);
  };

  const goPrev = () => {
    setStep((prev) => prev - 1);
  };

  useEffect(() => {
    setStep(0);
  }, [showSurveyResult]);

  const openState = useMemo(() => {
    return (
      showSurveyResult === true &&
      (showSurveyResultSkipped === false || isNil(showSurveyResultSkipped)) &&
      SHOW_SURVEY_RESULTS === true
    );
  }, [showSurveyResult, showSurveyResultSkipped]);

  return (
    <Dialog
      open={openState}
      classes={{
        paper: classes.dialogPaper,
        root: classes.dialogBackdrop,
      }}
      aria-labelledby="survey-results-dialog-title"
      hideBackdrop
      maxWidth="lg"
    >
      <DialogContent
        className={step === 0 ? classes.dialogContentHi : classes.dialogContent}
      >
        {step === 0 ? (
          <Box className={classes.hiBox}>
            <span
              role="img"
              aria-labelledby="hi"
              style={{ fontSize: "2.5rem" }}
            >
              👋 {t("SURVEYS_RESULT_HI_TITLE")}
            </span>
            <span
              role="img"
              aria-labelledby="heart"
              style={{ fontSize: "1.75rem" }}
            >
              💖 {t("SURVEYS_RESULT_HI_TITLE2")}
            </span>

            <span
              role="img"
              aria-labelledby="charts"
              style={{ fontSize: "1.5rem" }}
            >
              📊 {t("SURVEYS_RESULT_HI_SUBTITLE")}
            </span>
          </Box>
        ) : (
          <div className={classes.chartsContainer}>
            {step === 1 ? (
              <Box className={classes.supportChart}>
                <Typography variant="h3">
                  {t("SURVEYS_RESULT_DIALOG_SUPP_TITLE")}
                </Typography>
                <SupportSatisfactionChart />
              </Box>
            ) : step === 2 ? (
              <Box className={classes.solutionsChart}>
                <Typography variant="h3">
                  {t("SURVEYS_RESULT_DIALOG_SOLUTIONS_TITLE")}
                </Typography>
                <SolutionsSatisfactionChart />
              </Box>
            ) : (
              <Box>
                <span
                  role="img"
                  aria-labelledby="comments"
                  style={{ fontSize: "2rem" }}
                >
                  🎖️ {t("SURVEYS_RESULT_DIALOG_COMMENTS")}
                </span>
                <List className={classes.list}>
                  {COMMENTS.map(({ sem, label, solution }, i) => {
                    return (
                      <ListItemText key={i} className={classes.listItem}>{`${
                        sem === COMMENT_NEGATIVE
                          ? "🔴"
                          : sem === COMMENT_NEUTRAL
                          ? "🟡"
                          : "🟢"
                      }  ${
                        !isNil(solution) ? solution : ""
                      } "${label}"`}</ListItemText>
                    );
                  })}
                </List>
                <span
                  role="img"
                  aria-labelledby="comments"
                  style={{ fontSize: "2rem", marginTop: 16 }}
                >
                  ⏩ {t("SURVEYS_RESULT_DIALOG_NEXT_STEPS")}
                </span>
                <List className={classes.list}>
                  {NEXT_ACTIONS.map((x, i) => {
                    return (
                      <ListItemText
                        key={i}
                        className={classes.listItem}
                      >{`▶️ ${x}`}</ListItemText>
                    );
                  })}
                </List>
              </Box>
            )}
          </div>
        )}

        <Box className={classes.stepActions}>
          {(step === 0 || step === 3) && (
            <Button
              onClick={() => closeShowSurveyResultDialog()}
              variant="outlined"
              color="primary"
              startIcon={<CancelIcon />}
              size="large"
            >
              {step === 0 ? t("SURVEYS_NOT_NOW") : t("CLOSE")}
            </Button>
          )}

          {step > 0 && (
            <Button
              onClick={goPrev}
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              size="large"
            >
              {t("FORM_BACK")}
            </Button>
          )}

          {step >= 0 && (
            <Button
              onClick={goNext}
              variant="outlined"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              size="large"
              disabled={step === 3}
            >
              {t("NEXT")}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
